// ECONIC dates and times (DESIGN-SYSTEM §13.3, shell-ia §11.1). Owned by WP-F0-WEB, FROZEN after gate G0.
//
// Every time is shown in the Gulf Standard Time zone (Asia/Dubai) with its name ("21:14 GST" / «21:14 بتوقيت الخليج»), whatever the
// device's zone. Dates are words in both languages ("Monday 12 October 2026" / «الاثنين 12 أكتوبر 2026»), never abbreviated in Arabic, with
// Western digits. The Hijri date comes from the Umm al-Qura calendar ("1 Jumada I 1448 AH" / «1 جمادى الأولى 1448 هـ»).
// Input is an ISO string, epoch milliseconds or a Date; anything unreadable formats as "".

export type TimeLocale = "en" | "ar" | (string & {});
export type DateInput = string | number | Date | null | undefined;

export const GST_TIME_ZONE = "Asia/Dubai";

/** The zone's name after a time. */
export const GST_LABEL = { en: "GST", ar: "بتوقيت الخليج" } as const;

const isAr = (locale: TimeLocale) => String(locale).startsWith("ar");
const BIDI_MARKS = /[؜‎‏]/g;

export function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const cache = new Map<string, Intl.DateTimeFormat>();
function dtf(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, { timeZone: GST_TIME_ZONE, numberingSystem: "latn", ...options });
    cache.set(key, f);
  }
  return f;
}

const gregorian = (locale: TimeLocale) => (isAr(locale) ? "ar-AE" : "en-GB");

function parts(d: Date, locale: TimeLocale, options: Intl.DateTimeFormatOptions): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of dtf(gregorian(locale), options).formatToParts(d)) if (p.type !== "literal") out[p.type] = p.value.replace(BIDI_MARKS, "");
  return out;
}

/** "21:14" in Dubai time, 24-hour. */
export function formatTime(value: DateInput, locale: TimeLocale): string {
  const d = toDate(value);
  if (!d) return "";
  const p = parts(d, locale, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  return `${p.hour}:${p.minute}`;
}

/** "21:14 GST" / «21:14 بتوقيت الخليج». */
export function formatTimeGst(value: DateInput, locale: TimeLocale): string {
  const t = formatTime(value, locale);
  return t ? `${t} ${isAr(locale) ? GST_LABEL.ar : GST_LABEL.en}` : "";
}

/** "12 Oct" / «12 أكتوبر» (Arabic months are never abbreviated). */
export function formatDay(value: DateInput, locale: TimeLocale): string {
  const d = toDate(value);
  if (!d) return "";
  const p = parts(d, locale, { day: "numeric", month: isAr(locale) ? "long" : "short" });
  return `${p.day} ${p.month}`;
}

/** "12 October 2026" / «12 أكتوبر 2026». */
export function formatLongDate(value: DateInput, locale: TimeLocale): string {
  const d = toDate(value);
  if (!d) return "";
  const p = parts(d, locale, { day: "numeric", month: "long", year: "numeric" });
  return `${p.day} ${p.month} ${p.year}`;
}

/** "Monday 12 October 2026" / «الاثنين 12 أكتوبر 2026» (no comma after the weekday, DESIGN-SYSTEM §13.3). */
export function formatFullDate(value: DateInput, locale: TimeLocale): string {
  const d = toDate(value);
  if (!d) return "";
  const p = parts(d, locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return `${p.weekday} ${p.day} ${p.month} ${p.year}`;
}

/** "Sun 11 Oct, 21:14 GST" / «الأحد 11 أكتوبر، 21:14 بتوقيت الخليج» (last sign-in, audit rows). */
export function formatDateTimeGst(value: DateInput, locale: TimeLocale): string {
  const d = toDate(value);
  if (!d) return "";
  const ar = isAr(locale);
  const p = parts(d, locale, { weekday: ar ? "long" : "short", day: "numeric", month: ar ? "long" : "short" });
  return `${p.weekday} ${p.day} ${p.month}${ar ? "،" : ","} ${formatTimeGst(d, locale)}`;
}

/** The Umm al-Qura date: "1 Jumada I 1448 AH" / «1 جمادى الأولى 1448 هـ». */
export function formatHijri(value: DateInput, locale: TimeLocale): string {
  const d = toDate(value);
  if (!d) return "";
  const f = dtf(isAr(locale) ? "ar-AE-u-ca-islamic-umalqura" : "en-GB-u-ca-islamic-umalqura", { day: "numeric", month: "long", year: "numeric" });
  return f.format(d).replace(BIDI_MARKS, "");
}

/** The Dubai calendar day of an instant, "2026-10-12" (to compare days without the device's zone). */
export function dubaiDateKey(value: DateInput): string {
  const d = toDate(value);
  if (!d) return "";
  const p: Record<string, string> = {};
  for (const x of dtf("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d)) p[x.type] = x.value;
  return `${p.year}-${p.month}-${p.day}`;
}

const STEPS: [Intl.RelativeTimeFormatUnit, number][] = [["year", 31_536_000], ["month", 2_592_000], ["week", 604_800], ["day", 86_400], ["hour", 3_600], ["minute", 60]];

/** "3 hours ago" / «قبل 3 ساعات», "in 2 days"; under a minute reads "now". */
export function relativeTime(value: DateInput, locale: TimeLocale, now: DateInput = Date.now()): string {
  const d = toDate(value);
  const ref = toDate(now);
  if (!d || !ref) return "";
  const seconds = (d.getTime() - ref.getTime()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(isAr(locale) ? "ar-AE-u-nu-latn" : "en-GB", { numeric: "auto" });
  for (const [unit, size] of STEPS) if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit).replace(BIDI_MARKS, "");
  return rtf.format(0, "minute").replace(BIDI_MARKS, "");
}

/**
 * A month tick of a time axis (time runs left to right in both languages, C23): the year at January, otherwise the month
 * ("Feb" in English, «فبراير» in Arabic, where months are never abbreviated).
 */
export function monthTick(value: DateInput, locale: TimeLocale): string {
  const d = toDate(value);
  if (!d) return "";
  const p = parts(d, locale, { month: isAr(locale) ? "long" : "short", year: "numeric" });
  const m = parts(d, "en", { month: "numeric" }).month;
  return m === "1" ? p.year : p.month;
}
